import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import Loader from '~components/generic/loader/Loader';
import useLabels from '~hooks/useLabels';
import { deleteAsset } from '~modules/media-library/controller/api/assets';
import { AssetId } from '~modules/media-library/model/Asset';

type ConfirmDeleteProps = {
  onClose?: () => void;
  onDeleteSuccess?: () => void;
  assetId: string;
};

export function ConfirmDelete({ onClose, assetId, onDeleteSuccess }: ConfirmDeleteProps) {
  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Track if form submission is complete
  const [isComplete, setIsComplete] = useState<boolean>(false);

  // Track if form submission is failed
  const [isError, setIsError] = useState<boolean>(false);

  // Get translated labels
  const {
    hlAssetDeleteHeadline,
    msgAssetDeleteConfirm,
    msgAssetDeleteSuccess,
    msgAssetDeleteFailed,
    btnAssetDeleteClose,
    btnAssetDeleteConfirm,
    btnAssetDeleteCancel,
  } = useLabels([
    'hlAssetDeleteHeadline',
    'msgAssetDeleteConfirm',
    'msgAssetDeleteSuccess',
    'msgAssetDeleteFailed',
    'btnAssetDeleteClose',
    'btnAssetDeleteConfirm',
    'btnAssetDeleteCancel',
  ]);

  const useDeleteAsset = (assetId: AssetId) => {
    return useMutation({
      mutationFn: async () => await deleteAsset(assetId),
      onError: () => {
        setIsError(true);
        setIsLoading(false);
      },
      onSuccess: () => {
        setIsComplete(true);
        setIsLoading(false);
      },
    });
  };
  const { mutate } = useDeleteAsset(assetId);

  const onConfirm = useCallback(() => {
    setIsLoading(true);
    mutate();
  }, [mutate]);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ minHeight: '200px' }} className="flex justify-center">
        <Loader />
      </div>
    );
  }

  // Show success message
  if (isComplete) {
    return (
      <div>
        <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
          <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
            {hlAssetDeleteHeadline}
          </h2>
        </header>
        <div className="raise-request-assets-form container py-1 px-3">
          <p>{msgAssetDeleteSuccess}</p>
          <div className="flex my-4 justify-center">
            <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onDeleteSuccess}>
              {btnAssetDeleteClose}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
          <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
            {hlAssetDeleteHeadline}
          </h2>
        </header>
        <div className="raise-request-assets-form container py-1 px-3">
          <p>{msgAssetDeleteFailed}</p>
          <div className="flex my-4 justify-center">
            <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onClose}>
              {btnAssetDeleteClose}
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Render form
  return (
    <>
      <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
        <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
          {hlAssetDeleteHeadline}
        </h2>
      </header>
      <div className="raise-request-assets-form container py-1 px-3">
        <p>{msgAssetDeleteConfirm}</p>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onConfirm}>
            {btnAssetDeleteConfirm}
          </button>
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            {btnAssetDeleteCancel}
          </button>
        </div>
      </div>
    </>
  );
}
